// Preset catalog for repair intake wizard.
// Shops can extend later; UI always offers "Other…" free-text.

export const DEVICE_TYPES = [
  { id: "phone", label: "Phone" },
  { id: "tablet", label: "Tablet" },
  { id: "laptop", label: "Laptop" },
  { id: "watch", label: "Smartwatch" },
  { id: "other", label: "Other" },
];

export const MANUFACTURERS = [
  { id: "apple", label: "Apple" },
  { id: "samsung", label: "Samsung" },
  { id: "google", label: "Google" },
  { id: "oneplus", label: "OnePlus" },
  { id: "xiaomi", label: "Xiaomi" },
  { id: "huawei", label: "Huawei" },
  { id: "motorola", label: "Motorola" },
  { id: "sony", label: "Sony" },
  { id: "lg", label: "LG" },
  { id: "nokia", label: "Nokia" },
  { id: "oppo", label: "Oppo" },
  { id: "asus", label: "Asus" },
  { id: "other", label: "Other" },
];

// Curated list per (manufacturer, device_type). "other" always allowed via free text.
export const MODELS = {
  "apple|phone": [
    "iPhone 15 Pro Max", "iPhone 15 Pro", "iPhone 15 Plus", "iPhone 15",
    "iPhone 14 Pro Max", "iPhone 14 Pro", "iPhone 14 Plus", "iPhone 14",
    "iPhone 13 Pro Max", "iPhone 13 Pro", "iPhone 13", "iPhone 13 mini",
    "iPhone 12 Pro Max", "iPhone 12 Pro", "iPhone 12", "iPhone 12 mini",
    "iPhone 11 Pro Max", "iPhone 11 Pro", "iPhone 11",
    "iPhone SE (3rd gen)", "iPhone SE (2nd gen)", "iPhone XR", "iPhone XS",
  ],
  "apple|tablet": [
    "iPad Pro 12.9\"", "iPad Pro 11\"", "iPad Air", "iPad mini", "iPad (10th gen)", "iPad (9th gen)",
  ],
  "apple|laptop": [
    "MacBook Pro 16\"", "MacBook Pro 14\"", "MacBook Pro 13\"", "MacBook Air 15\"", "MacBook Air 13\"",
  ],
  "apple|watch": [
    "Apple Watch Ultra 2", "Apple Watch Series 9", "Apple Watch Series 8", "Apple Watch SE",
  ],
  "samsung|phone": [
    "Galaxy S24 Ultra", "Galaxy S24+", "Galaxy S24",
    "Galaxy S23 Ultra", "Galaxy S23+", "Galaxy S23",
    "Galaxy S22 Ultra", "Galaxy S22+", "Galaxy S22",
    "Galaxy Z Fold 5", "Galaxy Z Flip 5", "Galaxy Z Fold 4", "Galaxy Z Flip 4",
    "Galaxy Note 20 Ultra", "Galaxy A54", "Galaxy A34", "Galaxy A14",
  ],
  "samsung|tablet": [
    "Galaxy Tab S9 Ultra", "Galaxy Tab S9+", "Galaxy Tab S9", "Galaxy Tab A9+", "Galaxy Tab A8",
  ],
  "samsung|watch": [
    "Galaxy Watch 6 Classic", "Galaxy Watch 6", "Galaxy Watch 5 Pro", "Galaxy Watch 5",
  ],
  "google|phone": [
    "Pixel 8 Pro", "Pixel 8", "Pixel 7 Pro", "Pixel 7", "Pixel 7a",
    "Pixel 6 Pro", "Pixel 6", "Pixel 6a", "Pixel Fold",
  ],
  "oneplus|phone": [
    "OnePlus 12", "OnePlus 11", "OnePlus 10 Pro", "OnePlus 10T", "OnePlus Nord 3",
  ],
  "xiaomi|phone": [
    "Xiaomi 14 Pro", "Xiaomi 14", "Xiaomi 13 Pro", "Xiaomi 13", "Redmi Note 13 Pro", "Redmi Note 12",
  ],
  "huawei|phone": [
    "Huawei P60 Pro", "Huawei Mate 60 Pro", "Huawei P50 Pro",
  ],
  "motorola|phone": [
    "Moto Edge 40 Pro", "Moto Edge 40", "Moto G84", "Moto G73", "Razr 40 Ultra",
  ],
  "sony|phone": [
    "Xperia 1 V", "Xperia 5 V", "Xperia 10 V",
  ],
  "lg|phone": [
    "LG Wing", "LG Velvet", "LG V60 ThinQ",
  ],
  "nokia|phone": [
    "Nokia G60", "Nokia X30", "Nokia XR21",
  ],
  "oppo|phone": [
    "Oppo Find X6 Pro", "Oppo Reno 10 Pro", "Oppo A78",
  ],
  "asus|laptop": [
    "ROG Zephyrus G14", "ROG Zephyrus G16", "Zenbook 14", "Vivobook 16",
  ],
};

// Problems catalog. Prices are shop defaults — staff can override.
export const PROBLEMS = [
  { id: "screen", label: "Screen / LCD replacement", price: 149, icon: "screen" },
  { id: "battery", label: "Battery replacement", price: 69, icon: "battery" },
  { id: "charge_port", label: "Charging port repair", price: 79, icon: "plug" },
  { id: "camera", label: "Camera replacement", price: 89, icon: "camera" },
  { id: "back_glass", label: "Back glass replacement", price: 99, icon: "glass" },
  { id: "water_damage", label: "Water damage diagnostic", price: 49, icon: "drop" },
  { id: "software", label: "Software / OS issue", price: 39, icon: "cpu" },
  { id: "speaker", label: "Speaker / earpiece", price: 59, icon: "speaker" },
  { id: "microphone", label: "Microphone", price: 59, icon: "mic" },
  { id: "button", label: "Button (home / power / volume)", price: 49, icon: "button" },
  { id: "no_power", label: "Won't power on / diagnostic", price: 29, icon: "power" },
  { id: "data_recovery", label: "Data recovery", price: 129, icon: "disk" },
  { id: "other", label: "Other (describe in notes)", price: 0, icon: "dots" },
];

export function getModels(manufacturerId, deviceTypeId) {
  return MODELS[`${manufacturerId}|${deviceTypeId}`] || [];
}

// Canonical repair-type order used to arrange repairs on BOTH the ticket
// wizard's Issues step and the Inventory → Parts screen so they always match.
// Screen Repair first, then the common physical repairs; anything not listed
// keeps its original (website) order after these. A per-model saved order
// (catalog_display_order) always takes priority over this default.
export const REPAIR_TYPE_ORDER = [
  "screen-repair", "screen-repair-replacement", "screen-replacement", "lcd-replacement",
  "back-glass-replacement", "back-glass", "back-cover-replacement", "housing-replacement",
  "battery-replacement", "battery",
  "charging-port-replacement", "charging-port-repair",
  "rear-camera-replacement", "back-camera-replacement",
  "front-camera-replacement",
  "camera-glass-broken", "camera-lens-replacement",
  "speaker-repair", "ear-speaker-repair", "loudspeaker-repair", "earpiece-repair",
  "microphone-replacement",
  "power-volume-button", "power-button-repair", "volume-button-repair",
  "keyboard-replacement", "trackpad-replacement",
  "water-liquid-damage-repair", "water-damage",
  "motherboard-logicboard-repair",
  "not-turning-on",
  "software-update-restore",
];

// Sort a list of repairs by: explicit savedOrder → canonical order → original
// (stable) input order for the remainder. `idOf` reads the repair's id.
export function orderRepairs(repairs, { savedOrder = [], idOf = (r) => r.id } = {}) {
  const priority = new Map();
  let rank = 0;
  for (const id of savedOrder || []) if (!priority.has(id)) priority.set(id, rank++);
  for (const id of REPAIR_TYPE_ORDER) if (!priority.has(id)) priority.set(id, rank++);
  return repairs
    .map((r, i) => ({ r, i }))
    .sort((a, b) => {
      const ra = priority.has(idOf(a.r)) ? priority.get(idOf(a.r)) : Infinity;
      const rb = priority.has(idOf(b.r)) ? priority.get(idOf(b.r)) : Infinity;
      return ra !== rb ? ra - rb : a.i - b.i;
    })
    .map((x) => x.r);
}


// ── Repair list arrangement shared by the catalog manager and the wizard ──
// Scope keys live in the chain-wide catalog_display_order collection:
//   repairhidden:<mfr>:<dev>:<model>  → labels removed from this model
//   repairorder:<mfr>:<dev>:<model>   → explicit drag-and-drop label order
export const repairHiddenScope = (mfr, dev, mdl) => `repairhidden:${mfr || ""}:${dev || ""}:${mdl || ""}`;
export const repairOrderScope = (mfr, dev, mdl) => `repairorder:${mfr || ""}:${dev || ""}:${mdl || ""}`;

const normLabel = (v) => String(v || "").trim().toLowerCase();

/**
 * Resolve the layout that applies to one model. Order falls back
 * model → device → all devices (most specific wins); removals are the UNION
 * of the three levels, so "remove from all devices" hides everywhere while a
 * model-level removal only hides there.
 */
export function resolveRepairLayout(orders, mfr, dev, mdl) {
  const at = (s) => (orders || {})[s] || [];
  const model = at(repairOrderScope(mfr, dev, mdl));
  const device = at(repairOrderScope(mfr, dev, ""));
  const global = at(repairOrderScope("", "", ""));
  const labelOrder = model.length ? model : device.length ? device : global;
  const hiddenLabels = [...new Set([
    ...at(repairHiddenScope("", "", "")),
    ...at(repairHiddenScope(mfr, dev, "")),
    ...at(repairHiddenScope(mfr, dev, mdl)),
  ])];
  return { labelOrder, hiddenLabels };
}

/**
 * Fixed grouping, then the saved drag order inside each group.
 *
 * Group order is always: WEBSITE (synced) → CUSTOM → PRESET. Shops wanted
 * their live website repairs up top, their own additions next and the
 * built-in presets last, identically on the repair library, the parts screen
 * and the ticket wizard. An explicit drag order ranks items WITHIN a group —
 * labels that aren't in the saved order keep their natural position behind
 * the ranked ones, so an order saved on one device can't shuffle a different
 * device's repairs into the wrong place.
 */
export const REPAIR_GROUPS = { website: 0, custom: 1, preset: 2 };

export function arrangeProblems(items, {
  labelOrder = [], hiddenLabels = [],
  isCustom = (x) => !!x._custom,
  labelOf = (x) => x.label,
  groupOf = null,
} = {}) {
  // Removals apply to synced / preset rows only — a custom repair is deleted
  // through its own catalog record, never by hiding a same-named website row.
  const hidden = new Set((hiddenLabels || []).map(normLabel));
  const visible = (items || []).filter((it) => isCustom(it) || !hidden.has(normLabel(labelOf(it))));

  const group = groupOf || ((it) => (isCustom(it) ? REPAIR_GROUPS.custom : REPAIR_GROUPS.website));
  const rank = new Map((labelOrder || []).map((l, i) => [normLabel(l), i]));
  const UNRANKED = Number.MAX_SAFE_INTEGER;

  return visible
    .map((it, i) => ({ it, i, g: group(it), r: rank.has(normLabel(labelOf(it))) ? rank.get(normLabel(labelOf(it))) : UNRANKED }))
    .sort((a, b) => (a.g !== b.g ? a.g - b.g : a.r !== b.r ? a.r - b.r : a.i - b.i))
    .map((x) => x.it);
}

